/**
 * Strefy czasowe do wyboru w ustawieniach organizacji.
 *
 * Strefa organizacji (`organizations.settings.timezone`) jest jedynym źródłem
 * prawdy o tym, „która jest godzina" dla danego najemcy: czyta ją i mózg NEXUS-a
 * (blok ZEGAR w promptcie systemowym), i generator agendy po stronie bazy
 * (`agenda_resolve_org_timezone`), i dziennik z kamer. Dlatego pole musi
 * produkować POPRAWNY identyfikator IANA — wpisane z palca „UTC+2" cicho spadłoby
 * na strefę domyślną i firma z Kijowa dostawałaby warszawskie godziny.
 *
 * Lista idzie z `Intl.supportedValuesOf('timeZone')` (pełna baza IANA
 * przeglądarki); starsze silniki bez tej funkcji dostają krótką listę stref,
 * w których realnie działają najemcy platformy.
 */

/** Zapasowa lista, gdy przeglądarka nie zna `Intl.supportedValuesOf`. */
const STREFY_ZAPASOWE = [
  'Europe/Warsaw',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Kyiv',
  'Europe/Vilnius',
  'Europe/Prague',
  'Europe/Bratislava',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Helsinki',
  'Europe/Riga',
  'Europe/Tallinn',
  'Europe/Zurich',
  'Europe/Istanbul',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Asia/Dubai',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

/** Strefa używana, gdy organizacja nie wybrała własnej — musi się zgadzać z `STREFA_DOMYSLNA` w nexus-core. */
export const STREFA_DOMYSLNA = 'Europe/Warsaw';

/** Wszystkie strefy IANA znane przeglądarce, posortowane; z listą zapasową. */
export function listaStref(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (klucz: string) => string[] };
  try {
    const wszystkie = intl.supportedValuesOf?.('timeZone');
    if (wszystkie?.length) return [...wszystkie].sort();
  } catch {
    /* silnik bez pełnej bazy — schodzimy na listę zapasową */
  }
  return [...STREFY_ZAPASOWE].sort();
}

/** Strefa wykryta w przeglądarce — podpowiedź „ustaw jak u mnie". */
export function strefaPrzegladarki(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || STREFA_DOMYSLNA;
  } catch {
    return STREFA_DOMYSLNA;
  }
}

/** Czy ta strefa w ogóle istnieje w tym silniku. */
export function strefaPoprawna(strefa: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: strefa });
    return true;
  } catch {
    return false;
  }
}

/**
 * Podgląd „tak wygląda teraz w tej strefie" — jedyny sposób, żeby ktoś wybierający
 * z listy trzystu identyfikatorów zauważył, że pomylił się o kontynent.
 */
export function podgladCzasu(strefa: string, teraz: Date = new Date()): string {
  if (!strefaPoprawna(strefa)) return '';
  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: strefa,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(teraz);
}
